import {DatePipe} from '@angular/common';
import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DateFormatterService {
  constructor(private readonly datePipe: DatePipe) {}

  /**
   * Formats a UTC date string to the user's local timezone.
   * @param utcDate
   * @returns The date part formatted as e.g. "May 5, 2025".
   */
  formatDate(utcDate: string): string | null {
    const localDate = this.toLocalDate(utcDate);
    return this.datePipe.transform(localDate, 'MMMM d, yyyy');
  }

  /**
   * Formats a UTC date string to the user's local timezone.
   * @param utcDate
   * @returns The time part formatted as e.g., "12:00 AM".
   */
  formatTime(utcDate: string): string | null {
    const localDate = this.toLocalDate(utcDate);
    return this.datePipe.transform(localDate, 'h:mm a');
  }

  /**
   * Formats a UTC date string to the user's local timezone with the timezone abbreviation.
   * @param utcDate
   * @returns The time part formatted as e.g., "12:00 AM EDT".
   */
  formatTimeWithTimezone(utcDate: string): string | null {
    const localDate = this.toLocalDate(utcDate);

    if (localDate) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
      return formatter.format(localDate);
    }

    return null;
  }

  /**
   * Converts a UTC date string to a local Date object.
   * Returns null if the date string is invalid, null, or empty.
   */
  private toLocalDate(utcDate: string): Date | null {
    // Explicitly handle null or empty string inputs before Date constructor
    if (!utcDate) {
      return null;
    }
    const dateObj = new Date(utcDate);
    // The Date constructor will return an 'Invalid Date' if the string is not parse-able.
    // getTime() returns NaN for an invalid date object.
    return Number.isNaN(dateObj.getTime()) ? null : dateObj;
  }
}
